// src/hooks/data/useGatheringsForDateRange.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { Gathering } from '../../../../types';
import gatheringDataService from '../../../../services/data/gatheringService';
import { formatDateForApi } from '../../../../services/api/utils';

// Define the result interface
export interface UseGatheringsForDateRangeResult {
  gatherings: Gathering[];
  isLoading: boolean;
  isBackgroundLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useGatheringsForDateRange = (
  startDate: Date,
  endDate: Date,
  token: string | null,
  revalidateOnFocus: boolean = true
): UseGatheringsForDateRangeResult => {
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const lastFocusTime = useRef<number>(0);
  
  // Format dates for the store metadata
  const startKey = formatDateForApi(startDate);
  const endKey = formatDateForApi(endDate);
  const dateRangeKey = `${startKey}_${endKey}`;
  
  // Set token in the data service
  useEffect(() => {
    gatheringDataService.setToken(token);
  }, [token]);
  
  // Load data with SWR pattern
  const loadData = useCallback(async (background: boolean = false) => {
    if (!token) return;
    
    try {
      if (background) {
        setIsBackgroundLoading(true);
      } else {
        setIsInitialLoading(true);
      }
      
      // Always use loadGatheringsForDateRange for any date range
      await gatheringDataService.loadGatheringsForDateRange(startDate, endDate);
      
      setError(null);
    } catch (err) {
      setError('Failed to load gatherings for the selected date range');
    } finally {
      if (background) {
        setIsBackgroundLoading(false);
      } else {
        setIsInitialLoading(false);
      }
    }
  }, [token, startDate, endDate]);
  
  // Initial data loading
  useEffect(() => {
    if (!token) return;
    
    // Check if we have data for this date range
    const hasData = gatheringDataService.getGatheringsForDateRange(startDate, endDate).length > 0;
    
    if (!hasData) {
      // No data, do a normal load
      loadData(false);
    } else {
      // Data exists, no need to show loading state
      setIsInitialLoading(false);
    }
  }, [token, startDate, endDate, loadData]);
  
  // Set up revalidation triggers
  useEffect(() => {
    if (!token || !revalidateOnFocus) return;
    
    // Revalidate on window focus
    const handleFocus = () => {
      const now = Date.now();
      // Debounce focus events to prevent multiple rapid reloads
      if (now - lastFocusTime.current > 5000) { // 5 second debounce
        lastFocusTime.current = now;
        loadData(true); // Important: use background loading
      }
    };
    
    // Revalidate on reconnect
    const handleOnline = () => {
      loadData(true); // Important: use background loading
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [token, loadData, revalidateOnFocus]);
  
  // Get gatherings for this date range from the store
  const gatherings = gatheringDataService.getGatheringsForDateRange(startDate, endDate);
  
  return {
    gatherings,
    isLoading: isInitialLoading,
    isBackgroundLoading,
    error,
    refresh: async () => await loadData(false)
  };
};

export default useGatheringsForDateRange;